import type { TFunction } from 'i18next';
import { FC } from 'react';
import generateRandomId from './helpers/randomId';

export type Option = {
  label: string;
  value: string | number;
  active?: boolean;
};

export type FilterColumn = {
  name: string;
  label: string;
} & ColumnTypes;

export type CustomComponentInputs = {
  onChange: (value: TRule['value']) => void;
  value?: TRule['value'];
};

type OperatorPerTypeRegistry = {
  [key in Exclude<ColumnTypes['type'], null | 'custom'>]: ComparisonOperator[];
};

export type ColumnTypes =
  | { type: 'text' }
  | { type: 'number'; min?: number; max?: number; precision?: number }
  | { type: 'date' }
  | { type: 'date-range' }
  | { type: 'boolean'; options: Option[] }
  | {
      type: 'dropdown';
      multipleSelect?: boolean;
      allowClear?: boolean;
      allowSearch?: boolean;
      options: Option[];
    }
  | {
      type: 'array-text';
      placeholder?: string;
    }
  | {
      type: 'custom';
      component: FC<CustomComponentInputs>;
      operators: ComparisonOperator[];
    }
  | { type: null };

export type Filter = {
  id: string;
  operator: JoinOperator | null;
  rules: TRule[];
};
export type TRule = {
  id: string;
  joinOperator?: JoinOperator | null;
  rules: TRule[];
  //TODO: col must be key of column name list
  col: string | null;
  operator: ComparisonOperator | null;
  value: TRuleValue;
};

export type TRuleValue = number | string | boolean | Date | Array<number> | Array<string> | null;

export type JoinOperator = '&&' | '||' | null;
export type ComparisonOperator =
  | '=='
  | '>'
  | '<'
  | '>='
  | '<='
  | '!='
  | 'contains'
  | 'contains_in'
  | 'overlap'
  | 'contained_in';

export const ComparisonOperatorsPerType: OperatorPerTypeRegistry = {
  text: ['==', '!=', 'contains'],
  number: ['==', '<', '>', '>=', '<='],
  date: ['==', '>=', '<='],
  'date-range': ['==', '>=', '<='],
  boolean: ['==', '!='],
  dropdown: ['==', '!='],
  'array-text': ['==', 'contains_in', 'overlap', 'contained_in'],
};
export type OperatorList = {
  label: string;
  value: ComparisonOperator;
}[];

export const ComparisonOperatorsList: {
  label: string;
  value: ComparisonOperator;
}[] = [
  { label: 'Is', value: '==' },
  { label: 'Is not', value: '!=' },
  { label: 'Greater than', value: '>' },
  { label: 'Less than', value: '<' },
  { label: 'Greater equal', value: '>=' },
  { label: 'Less equal', value: '<=' },
  { label: 'Contains', value: 'contains' },
  { label: 'Contains', value: 'contains_in' },
  { label: 'Has any of', value: 'overlap' },
  { label: 'Has all of', value: 'contained_in' },
];

export const JoinOperatorList: {
  label: string;
  value: JoinOperator;
}[] = [
  { label: 'AND', value: '&&' },
  { label: 'OR', value: '||' },
];

export const DefaultJoinOperator: JoinOperator = '&&';
export const InitalFilter: () => Filter = () => {
  return {
    id: generateRandomId(6),
    operator: '&&',
    rules: [InitalRule()],
  };
};

export const InitalRule: () => TRule = () => ({
  id: generateRandomId(6),
  col: null,
  operator: '==',
  value: null,
  rules: [],
  joinOperator: '&&',
});

export const GetOperatorsForType = (column: FilterColumn) => {
  const type = column.type;
  if (type === null) return [];
  let operators: ComparisonOperator[] = [];

  if (type === 'custom') {
    operators = column.operators;
  } else {
    operators = ComparisonOperatorsPerType[type];
  }

  return operators.map((d) => ComparisonOperatorsList.filter((cmp) => cmp.value === d)[0]);
};

export const getComparisonOperatorsList = (t: TFunction): { label: string; value: ComparisonOperator }[] => [
  { label: t('filters-op-is'), value: '==' },
  { label: t('filters-op-is-not'), value: '!=' },
  { label: t('filters-op-greater-than'), value: '>' },
  { label: t('filters-op-less-than'), value: '<' },
  { label: t('filters-op-greater-equal'), value: '>=' },
  { label: t('filters-op-less-equal'), value: '<=' },
  { label: t('filters-op-contains'), value: 'contains' },
  { label: t('filters-op-contains'), value: 'contains_in' },
  { label: t('filters-op-has-any-of'), value: 'overlap' },
  { label: t('filters-op-has-all-of'), value: 'contained_in' },
];

export const getJoinOperatorList = (t: TFunction): { label: string; value: JoinOperator }[] => [
  { label: t('filters-join-and'), value: '&&' },
  { label: t('filters-join-or'), value: '||' },
];

export const getOperatorsForType = (column: FilterColumn, t: TFunction): OperatorList => {
  const type = column.type;
  if (type === null) return [];
  let operators: ComparisonOperator[] = [];

  if (type === 'custom') {
    operators = column.operators;
  } else {
    operators = ComparisonOperatorsPerType[type];
  }

  const list = getComparisonOperatorsList(t);
  return operators.map((d) => list.find((cmp) => cmp.value === d)!).filter(Boolean);
};
